<template>
    <AppHead title="Transfer Partners Guide" />
    <div
        class="px-4 md:px-8 relative"
        data-page="resources.transfer-partners-guide"
    >
        <!-- Transfer Bonus Alerts Upsell Banner (freemium users only) -->
        <TransferBonusAlertBanner
            v-if="transferBonusAlertsEnabled && !isSubscribed"
            :banner-dismissed="bannerDismissed"
            :dismiss-url="dismissUrl"
            class="mb-6"
        />

        <div v-show="!navigatingToProgram">
            <!-- Header Section -->
            <div class="w-full">
                <div
                    class="flex flex-col md:flex-row md:items-start md:justify-between mb-8 md:mb-12"
                >
                    <div class="flex-1">
                        <h1
                            class="text-slate-800 dark:text-slate-100 text-2xl md:text-2xl font-semibold leading-8"
                        >
                            {{
                                filters.favorites
                                    ? 'Favorites'
                                    : 'Transfer partners guide'
                            }}
                        </h1>
                        <p
                            :class="[
                                'mt-2 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5',
                                filters.favorites ? 'invisible' : 'visible',
                            ]"
                        >
                            Learn how to turn your credit card points into hotel
                            points or airline miles and get maximum value.
                        </p>
                        <!-- Mobile Favorites button (below title/description) -->
                        <div class="mt-4 md:hidden">
                            <button
                                @click="toggleFavoritesFilter()"
                                class="flex items-center gap-2 px-4 py-2 h-9 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                <Icon
                                    :icon="
                                        filters.favorites
                                            ? 'heart'
                                            : 'unfavorited'
                                    "
                                    :class="[
                                        'w-5 h-5',
                                        filters.favorites
                                            ? 'text-red-500'
                                            : 'text-slate-800 dark:text-slate-100',
                                    ]"
                                />
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Favorites
                                </span>
                            </button>
                        </div>
                    </div>
                    <!-- Desktop Favorites button (right aligned) -->
                    <div class="hidden md:flex items-center">
                        <button
                            @click="toggleFavoritesFilter()"
                            class="flex items-center gap-2 px-4 py-2.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <Icon
                                :icon="
                                    filters.favorites ? 'heart' : 'unfavorited'
                                "
                                :class="[
                                    'w-5 h-5',
                                    filters.favorites
                                        ? 'text-red-500'
                                        : 'text-slate-800 dark:text-slate-100',
                                ]"
                            />
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                            >
                                Favorites
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Main Content -->
            <div class="w-full">
                <TransferPartnerFilters
                    :filters="filters"
                    :searchQuery="searchQuery"
                    :selectedProgramTypes="selectedProgramTypes"
                    :selectedAddedToMyPoints="selectedAddedToMyPoints"
                    :singleUserToggle="singleUserToggle"
                    :isSingleUser="isSingleUser"
                    :programTypeOptions="programTypeOptions"
                    :addedToMyPointsOptions="addedToMyPointsOptions"
                    :hasFilterSelections="hasFilterSelections"
                    :activeFiltersCount="activeFiltersCount"
                    :paginatedPrograms="paginatedPrograms"
                    :sortOptions="sortOptions"
                    :sortButtonText="sortButtonText"
                    :groupOptions="groupOptions"
                    :groupButtonText="groupButtonText"
                    :showSortOverlay="showSortOverlay"
                    :showGroupOverlay="showGroupOverlay"
                    :tempSelectedSort="tempSelectedSort"
                    :tempSelectedGroup="tempSelectedGroup"
                    :isProgramTypeSelected="isProgramTypeSelected"
                    :isAddedToMyPointsSelected="isAddedToMyPointsSelected"
                    :toggleProgramTypeSelection="toggleProgramTypeSelection"
                    :toggleAddedToMyPointsSelection="
                        toggleAddedToMyPointsSelection
                    "
                    :resetProgramTypeFilter="resetProgramTypeFilter"
                    :resetAddedToMyPointsFilter="resetAddedToMyPointsFilter"
                    :clearAllFilters="clearAllFilters"
                    :clearSearch="clearSearch"
                    :updateFilters="updateFilters"
                    :selectSortOption="selectSortOption"
                    :selectGroupOption="selectGroupOption"
                    :openSortOverlay="openSortOverlay"
                    :closeSortOverlay="closeSortOverlay"
                    :applySortOverlay="applySortOverlay"
                    :openGroupOverlay="openGroupOverlay"
                    :closeGroupOverlay="closeGroupOverlay"
                    :applyGroupOverlay="applyGroupOverlay"
                    @update:searchQuery="searchQuery = $event"
                    @update:singleUserToggle="singleUserToggle = $event"
                    @update:tempSelectedSort="tempSelectedSort = $event"
                    @update:tempSelectedGroup="tempSelectedGroup = $event"
                    @open-mobile-filters="isMobileFiltersOpen = true"
                />

                <TransferPartnerGrid
                    :loading="loading"
                    :filters="filters"
                    :isMobile="isMobile"
                    :paginatedPrograms="paginatedPrograms"
                    :sortedPrograms="sortedPrograms"
                    :groupedPrograms="groupedPrograms"
                    :allPrograms="allPrograms"
                    :hasFilterSelections="hasFilterSelections"
                    :debouncedQuery="debouncedQuery"
                    :alertsFeatureEnabled="alertsFeatureEnabled"
                    :alertsEnabledProgramIds="alertsEnabledProgramIds"
                    :imageError="imageError"
                    :formatProgramType="formatProgramType"
                    :getTypeDisplayInfo="getTypeDisplayInfo"
                    :clearAllFilters="clearAllFilters"
                    :currentPage="currentPage"
                    :totalPages="totalPages"
                    :itemsPerPage="itemsPerPage"
                    :startIndex="startIndex"
                    :endIndex="endIndex"
                    :mobilePages="mobilePages"
                    @toggle-favorite="toggleFavorite($event)"
                    @navigate="navigateToProgram($event)"
                    @image-error="handleImageError($event)"
                    @page-change="handlePageChange($event)"
                    @toggle-favorites-filter="toggleFavoritesFilter()"
                />
            </div>

            <TransferPartnerMobileFilters
                :isOpen="isMobileFiltersOpen"
                :programTypeOptions="programTypeOptions"
                :addedToMyPointsOptions="addedToMyPointsOptions"
                :selectedProgramTypes="selectedProgramTypes"
                :selectedAddedToMyPoints="selectedAddedToMyPoints"
                :singleUserToggle="singleUserToggle"
                :isSingleUser="isSingleUser"
                :isProgramTypeSelected="isProgramTypeSelected"
                :isAddedToMyPointsSelected="isAddedToMyPointsSelected"
                :toggleProgramTypeSelection="toggleProgramTypeSelection"
                :toggleAddedToMyPointsSelection="toggleAddedToMyPointsSelection"
                :handleClearAllMobile="handleClearAllMobile"
                :applyMobileFilters="applyMobileFilters"
                @close="isMobileFiltersOpen = false"
                @update:singleUserToggle="singleUserToggle = $event"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { usePage, router } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useFavorite } from '@/composables/useFavorite';
import { useSubscription } from '@/composables/useSubscription';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useTransferPartnerGuideFilters } from '@/composables/useTransferPartnerGuideFilters';
import TransferPartnerFilters from './TransferPartnersGuide/TransferPartnerFilters.vue';
import TransferPartnerGrid from './TransferPartnersGuide/TransferPartnerGrid.vue';
import TransferPartnerMobileFilters from './TransferPartnersGuide/TransferPartnerMobileFilters.vue';
import TransferBonusAlertBanner from './ProgramPage/TransferBonusAlertBanner.vue';

defineOptions({
    layout: Layout,
});

const page = usePage();

const transferPrograms = computed(
    () =>
        page.props.transferPrograms || {
            data: [],
            pagination: {},
            total_count: 0,
        }
);
const userPrograms = computed(() => page.props.userPrograms || []);
const alertsEnabledProgramIds = computed(
    () => page.props.alertsEnabledProgramIds || []
);
const { isSubscribed } = useSubscription();
const { transferBonusAlertsEnabled } = useFeatureFlags();

// Banner dismissal state from backend
const bannerDismissed = computed(
    () => page.props.bonusAlertsBannerDismissed ?? false
);
const dismissUrl = computed(
    () => page.props.dismissBannerUrl ?? '/bonus-alerts/dismiss-banner'
);

// Bonus alerts are a 10xTravel+ feature, so the "alert added" indicator only
// surfaces for paid users (who are the only ones able to enable an alert).
const alertsFeatureEnabled = computed(
    () => !!page.props.features?.alerts_enabled && isSubscribed.value
);

// Composables
const {
    isMobile,
    filters,
    loading,
    searchQuery,
    debouncedQuery,
    selectedProgramTypes,
    selectedAddedToMyPoints,
    singleUserToggle,
    isSingleUser,
    programTypeOptions,
    addedToMyPointsOptions,
    isMobileFiltersOpen,
    sortOptions,
    sortButtonText,
    groupOptions,
    groupButtonText,
    showSortOverlay,
    showGroupOverlay,
    tempSelectedSort,
    tempSelectedGroup,
    currentPage,
    itemsPerPage,
    totalPages,
    allPrograms,
    paginatedPrograms,
    startIndex,
    endIndex,
    mobilePages,
    sortedPrograms,
    groupedPrograms,
    hasFilterSelections,
    activeFiltersCount,
    handlePageChange,
    isProgramTypeSelected,
    isAddedToMyPointsSelected,
    toggleProgramTypeSelection,
    toggleAddedToMyPointsSelection,
    resetProgramTypeFilter,
    resetAddedToMyPointsFilter,
    clearAllFilters,
    clearSearch,
    updateFilters,
    toggleFavoritesFilter,
    applyMobileFilters,
    handleClearAllMobile,
    selectSortOption,
    selectGroupOption,
    openSortOverlay,
    closeSortOverlay,
    applySortOverlay,
    openGroupOverlay,
    closeGroupOverlay,
    applyGroupOverlay,
    formatProgramType,
    getTypeDisplayInfo,
} = useTransferPartnerGuideFilters(page, transferPrograms, userPrograms);

const { toggleFavorite } = useFavorite(transferPrograms);

// Image error handling
const imageError = ref({});
const handleImageError = (partnerId) => {
    imageError.value[partnerId] = true;
};

// Navigation
const navigatingToProgram = ref(false);
const navigateToProgram = (program) => {
    if (!program || !program.slug) return;
    navigatingToProgram.value = true;
    try {
        if (
            typeof window !== 'undefined' &&
            window.__routeLoader &&
            typeof window.__routeLoader.show === 'function'
        ) {
            window.__routeLoader.show();
        }
    } catch (error) {
        console.error('Error showing route loader:', error);
    }

    const hideRouteLoader = () => {
        navigatingToProgram.value = false;
        try {
            if (
                typeof window !== 'undefined' &&
                window.__routeLoader &&
                typeof window.__routeLoader.hide === 'function'
            ) {
                window.__routeLoader.hide();
            }
        } catch (error) {
            console.error('Error hiding route loader:', error);
        }
    };

    router.get(
        `/resources/transfer-partners-guide/${program.slug}`,
        {},
        {
            preserveState: false,
            preserveScroll: true,
            onFinish: hideRouteLoader,
            onCancel: hideRouteLoader,
            onError: hideRouteLoader,
        }
    );
};
</script>
